import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { orgApi, saveOrganizerToken, clearOrganizerToken, readOrganizerToken } from "../lib/organizerApi";

const OrganizerCtx = createContext(null);

export function OrganizerAuthProvider({ children }) {
  const [organizer, setOrganizer] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    const jwt = readOrganizerToken();
    if (!jwt) {
      setOrganizer(null);
      setChecking(false);
      return null;
    }
    try {
      const { data } = await orgApi.get("/organizer/me");
      setOrganizer(data.organizer);
      setChecking(false);
      return data.organizer;
    } catch (_err) {
      clearOrganizerToken();
      setOrganizer(null);
      setChecking(false);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signup = useCallback(async (payload) => {
    const { data } = await orgApi.post("/organizer/signup", payload);
    saveOrganizerToken(data.access_token);
    setOrganizer(data.organizer);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await orgApi.post("/organizer/login", { email, password });
    saveOrganizerToken(data.access_token);
    setOrganizer(data.organizer);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearOrganizerToken();
    setOrganizer(null);
  }, []);

  const value = useMemo(
    () => ({ organizer, checking, signup, login, logout, refresh }),
    [organizer, checking, signup, login, logout, refresh]
  );
  return <OrganizerCtx.Provider value={value}>{children}</OrganizerCtx.Provider>;
}

export function useOrganizerAuth() {
  const ctx = useContext(OrganizerCtx);
  if (!ctx) throw new Error("useOrganizerAuth must be used inside OrganizerAuthProvider");
  return ctx;
}
